import { type Ref, createApp, type App, defineComponent, h, ref } from 'vue-demi'
import { type ExperienceActivationPayload } from '../../../../../../types/models'
import { type IVueActivationTargetRenderer } from './activation-target-renderer'
import { ActivationPreviewList } from '@source-digital/activation-list-vue'
import { createAndInsertElement } from '../../../../../../utils/insert-to-dom'
import { BaseVueActivationTargetRenderer } from './base'
import { type ExtractProps } from '../../../../../../types/helper'
import { type IDetailsOverlayTarget } from './details-overlay-target'
import { type MaybeElementRef, type UseElementBoundingReturn } from '@vueuse/core'
import { useElementBoundingPatch as useElementBounding } from '../utils/patches/use-element-bounding-patch'

export class VueActivationTargetBottomRenderer extends BaseVueActivationTargetRenderer implements IVueActivationTargetRenderer, IDetailsOverlayTarget {
  items: Ref<ExperienceActivationPayload[]>
  renderTargetEl: HTMLElement
  vueApp: App
  vueInstance: Vue
  vueRootElRef: MaybeElementRef
  elementBounding: UseElementBoundingReturn
  constructor (items: Ref<ExperienceActivationPayload[]>, ...args: ConstructorParameters<typeof BaseVueActivationTargetRenderer>) {
    super(...args)
    this.items = items
  }

  async initialize () {
    const renderTargetEl = createAndInsertElement(
      this.ctx.targetEl,
      this.ctx.settings.direct?.layout?.initOptions?.bottom,
      () => this.createElement('div', undefined, { role: 'bottom' })
    )
    this.vueRootElRef = ref()
    this.vueApp = createApp(this.#createComponent())
    this.vueInstance = this.vueApp.mount(renderTargetEl)
  }

  /**
   * Lazily initialize element bounding
   * @returns element bounding of the render target element
   */
  getElementBounding (): UseElementBoundingReturn {
    if (this.elementBounding == null) {
      this.elementBounding = useElementBounding(this.vueRootElRef)
    }
    return this.elementBounding
  }

  #createComponent () {
    const attrs = this.ctx.getComponentMarkerAttributes('bottom')
    // temporary solution for ad block
    return defineComponent<{ rootElRef: HTMLElement }>({
      name: 'EmbedderActivationTargetBottom',
      provide: this.createProvideObject(),
      attrs: {
        ...attrs
      },
      setup: () => ({
        rootElRef: this.vueRootElRef
      }),
      render: () => {
        if (this.hide.value) return h()
        if (this.detailsRenderer.isOpen.value) {
          const detailsTarget = this.currentLayout.value?.details?.target
          if (detailsTarget?.mode === 'bottom') {
            return h('div', {
              ref: 'rootElRef',
              class: this.rootClass,
              style: {
                width: `${this.videoElBounding.width.value}px`,
                ...(this.currentLayout.value?.targets?.bottom?.style ?? {}),
                ...(detailsTarget?.targetStyle ?? {})
              },
              attrs
            })
          } else {
            // FUTURE: allow previews to show while details is open if details target is not bottom
            return h('div', {
              style: {
                width: `${this.videoElBounding.width.value}px`,
                ...(this.currentLayout.value?.targets?.bottom?.style ?? {})
              },
              attrs
            })
          }
        }
        const body = h(ActivationPreviewList, {
          ref: 'rootElRef',
          class: this.rootClass,
          attrs,
          props: {
            items: this.items.value,
            options: {
              left: this.currentLayout.value?.targets?.bottom?.align === 'left',
              align: this.currentLayout.value?.targets?.bottom?.align,
              portrait: true,
              animation: this.currentLayout.value?.targets?.bottom?.animation ?? this.directSettings.options?.animation
            }
          } satisfies ExtractProps<typeof ActivationPreviewList>,
          style: {
            width: `${this.videoElBounding.width.value}px`,
            ...(this.currentLayout.value?.targets?.bottom?.style ?? {})
          },
          on: {
            ...this.createDefaultListeners()
          }
        })
        return body
      }
    })
  }

  destroy () {
    if (this.vueApp != null) this.vueApp?.unmount?.()
  }
}
